import React from "react";

import { getDispensary } from "api/requests/getDispensary";
import getDispensaryReviews from "api/requests/getDispensaryReviews";
import { DISPENSARY_PAGES } from "constants/dispensary";
import { Dispensary } from "custom-types/Dispensary";
import { DispensaryReview } from "custom-types/Reviews";
import { doctorsMetadata } from "lib/dispensaryMetadata";
import { createGetServerSideProps } from "utils/createGetServerSideProps";
import isDispensaryUnclaimed from "utils/dispensary/isDispensaryUnclaimed";
import generateDispensaryRedirect from "utils/generateDispensaryRedirect";
import { getCountryCodeHeader } from "utils/requestContext";

import AboutDispensary from "components/Dispensary/AboutDispensary";
import DispensaryHeader from "components/Dispensary/DispensaryHeader";
import DispensaryTabs from "components/Dispensary/DispensaryTabs";
import DoctorClaimPanel from "components/Dispensary/DoctorClaimPanel";
import MetaTags from "components/MetaTags";

type DoctorPageProps = {
  dispensary: Dispensary;
  reviews: DispensaryReview[];
};

const DoctorPage: React.FC<DoctorPageProps> = ({ dispensary, reviews }) => {
  const { coverPhotoUrl, logoUrl, name } = dispensary;
  const { description, image, title } = doctorsMetadata(
    coverPhotoUrl,
    logoUrl,
    name,
  );
  const isUnclaimed = isDispensaryUnclaimed(dispensary);

  return (
    <>
      <MetaTags description={description} image={image} title={title} />
      <DispensaryHeader dispensary={dispensary} isMainPage={true} />
      <DispensaryTabs
        activeTab={DISPENSARY_PAGES.main}
        dispensary={dispensary}
      />
      {isUnclaimed ? (
        <DoctorClaimPanel />
      ) : (
        <AboutDispensary dispensary={dispensary} reviews={reviews} />
      )}
    </>
  );
};

export const getServerSideProps = createGetServerSideProps<DoctorPageProps>(
  // @ts-expect-error -- TODO: Fix return type to adhere to props shape -- https://leafly.atlassian.net/browse/NL-1564
  async (context) => {
    const { query, req } = context;
    const { slug } = query;

    const domainCountryCode = getCountryCodeHeader(req.headers);

    const dispensary = await getDispensary(slug as string);

    const reviewsData = await getDispensaryReviews(
      (dispensary as Dispensary).slug,
      0,
      4,
    );

    const { reviews } = reviewsData;

    const redirect = generateDispensaryRedirect(
      domainCountryCode,
      context.resolvedUrl,
      context.query,
      dispensary,
    );

    if (redirect) {
      return { redirect };
    }

    return {
      props: { dispensary, reviews },
    };
  },
);

export default DoctorPage;
